import React from "react";
import styled, { css } from "styled-components";
import PathwaySelectMenu from "../Components/Inputs/PathwaySelectMenu";

export const PathwayCheckBoxFormGroup = styled.div`
    display: grid;
    grid-template-columns: 0.1fr 0.9fr;
    align-items: center;
    justify-items: start;
    column-gap: var(--gap-s);
    margin: 0 !important;

    & > input {
        margin: unset;
        position: unset;
        height: 1rem;
    }

    & > label {
        margin-bottom: 0 !important;
    }
`;

export const PathwayFormGroup = styled.div`
    position: relative;
    display: grid;
    /* The column spans the row. Without this it took its content's width, so a child asking for width:100% got
       the width of the widest child instead — which collapsed the integration grid in the connection dialog to
       a single column. justify-items:start still keeps ordinary inputs at their own size. */
    grid-template-columns: minmax(0, 1fr);
    row-gap: var(--gap-s);

    align-items: center;
    justify-items: start;
    margin: 0 !important;

    & > input {
        margin: unset;
    }

    & > label {
        margin-bottom: 0 !important;
    }

    & .invalid-feedback {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin: 0;
        padding-top: var(--gap-s);
    }
`;

//used for Forms with validation of <PathwayTextInputGroup>
export const PathwayFormGrid = styled.div`
    display: grid;
    row-gap: var(--gap-xl);
`;

//used for Forms without validation of <PathwayTextInputGroup>
export const PathwayFormGridSmall = styled.div`
    display: grid;
    row-gap: var(--gap-m);
`;

export const PathwayCheckbox = styled.input.attrs({ type: "checkbox" })`
    margin: unset;
    position: unset;
    width: 17px;
    height: 17px;
    min-width: 17px;
    min-height: 17px;
    flex: none;
    align-self: center;
    border: 1px solid var(--border-strong);
    border-radius: 4px;
    background-color: var(--surface-card);
    cursor: pointer;
    transition: background-color var(--pw-transition-fast) var(--pw-ease),
        border-color var(--pw-transition-fast) var(--pw-ease);

    &:checked {
        background-color: var(--pw-brand-cyan);
        border-color: var(--pw-brand-cyan);
    }

    &:focus {
        border-color: var(--pw-brand-cyan);
        box-shadow: none;
        outline: none;
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;

// Single canonical label — every form field label in the app uses this.
// PathwayFieldLabel is an alias kept for existing call sites; import either.
export const PathwayLabel = styled.label`
    margin: 0;
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-regular);
    color: var(--text-heading);
    line-height: var(--pw-leading-snug);
`;

export const AlineLabelWithExtra = styled.div`
    display: grid;
    grid-template-columns: min-content min-content;
`;

export const PathwayForm = styled.form.attrs(({ onSubmit }) => ({
    onSubmit: onSubmit ?? ((e) => e.preventDefault()),
}))`
    display: grid;
    column-gap: 1em;
    row-gap: 1em;
`;

export const PathwayCheckBoxFormGroupEndAligned = styled(PathwayCheckBoxFormGroup)`
    grid-template-columns: min-content max-content;
`;

export const PathwayFormGroupAligned = styled.div`
    margin: 0;
    display: grid;
    grid-template-columns: 0.4fr 0.6fr;
    align-items: center;
    justify-items: start;
    column-gap: 0.25rem;
`;

// Field chrome using our own design tokens — no Bootstrap class dependency.
const fieldChrome = css`
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: var(--pw-control-height);
    padding: 6px 11px;
    border: 1px solid var(--border-strong);
    border-radius: var(--pw-radius-lg);
    background-color: var(--surface-card);
    color: var(--text-heading);
    font-family: var(--font-body);
    font-size: var(--pw-text-control);
    font-weight: var(--pw-weight-light);
    line-height: var(--pw-leading-normal);
    transition: border-color var(--pw-transition-fast) var(--pw-ease);

    &::placeholder {
        color: var(--text-subtle);
    }

    &:focus {
        border-color: var(--pw-brand-cyan);
        box-shadow: none;
        outline: none;
    }

    &:disabled,
    &[readonly] {
        background-color: var(--pw-neutral-100);
        color: var(--text-muted);
    }

    &.is-invalid {
        border-color: var(--pw-danger);
        background-image: none;
        padding-right: 11px;

        &:focus {
            box-shadow: none;
        }
    }

    &.is-valid {
        background-image: none;
        padding-right: 11px;
    }

    &.pw-mono {
        font-family: var(--font-mono);
        font-size: var(--pw-text-base);
    }
`;

const NativeInput = styled.input`
    ${fieldChrome}
`;

// Same chrome, but the height must follow `rows` instead of the control height.
const NativeTextArea = styled.textarea`
    ${fieldChrome}
    min-height: unset;
    resize: vertical;
`;

// A drop-in replacement for reactstrap's Input: identical for every type except select
// (which renders our menu), textarea (which must be a real <textarea>, not
// <input type="textarea">) and radio/checkbox (which must not get text-input styles).
export const PathwayInput = ({ type, children, ...props }) => {
    if (type === "select") {
        return (
            <PathwaySelectMenu
                name={props.name}
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                disabled={props.disabled}
                invalid={props.invalid}
                dataCy={props["data-cy"]}
                className={props.className}
            >
                {children}
            </PathwaySelectMenu>
        );
    }
    if (type === "textarea") {
        return <NativeTextArea {...props}>{children}</NativeTextArea>;
    }
    if (type === "radio" || type === "checkbox") {
        // NativeInput applies text-input styles (width:100%, min-height, padding, border)
        // which make radio/checkbox controls enormous. Use a plain <input> instead.
        return <input type={type} {...props} />;
    }
    return <NativeInput type={type} {...props}>{children}</NativeInput>;
};

// PathwayStyledInput is used by PathwayTextInputGroup which passes `invalid`, `valid`, and
// `innerRef` (reactstrap-style ref prop).  We need to:
//   1. Convert invalid/valid booleans → is-invalid/is-valid class names that NativeInput styles.
//   2. Forward innerRef (and the standard React ref) to the underlying DOM element.
//   3. Keep non-DOM props (invalid, valid, innerRef) out of the spread to avoid React warnings.
export const PathwayStyledInput = React.forwardRef(
    ({ type, children, invalid, valid, innerRef, className, ...props }, forwardedRef) => {
        const classes = [
            className,
            invalid ? "is-invalid" : undefined,
            valid ? "is-valid" : undefined,
        ]
            .filter(Boolean)
            .join(" ") || undefined;

        const resolvedRef = innerRef || forwardedRef;

        if (type === "textarea") {
            return (
                <NativeTextArea ref={resolvedRef} className={classes} {...props}>
                    {children}
                </NativeTextArea>
            );
        }

        return type === "select" ? (
            <PathwaySelectMenu
                name={props.name}
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                disabled={props.disabled}
                invalid={invalid}
                dataCy={props["data-cy"]}
                className={classes}
            >
                {children}
            </PathwaySelectMenu>
        ) : (
            <NativeInput ref={resolvedRef} type={type} className={classes} {...props}>
                {children}
            </NativeInput>
        );
    }
);

// Alias — identical to PathwayLabel. Use either; they are the same component.
export const PathwayFieldLabel = PathwayLabel;
